import { bareName } from '~/lib/content-disposition';
import type { CanvasItem } from './collect-items';

/**
 * FRONT-2669 — what the Canvas header's Download button does for each kind of item.
 *  - document: the SAVED body, saved as Markdown from what the panel already holds
 *    (the backend has no document export route; `/api/documents` returns the body).
 *  - file (a Markdown file in Files): its existing raw route, `/api/files/<id>/raw`
 *    (answers with `Content-Disposition: attachment`).
 *  - sketch / deck: the served app's own HTML through the Apps download route.
 * Every kind gets the button, so there is no "no target" case.
 */
export type DownloadTarget =
  | { kind: 'document'; filename: string }
  | { kind: 'file'; url: string; filename: string }
  | { kind: 'app'; path: string; title: string; fallbackWord: 'sketch' | 'deck' };

/** The name a saved Markdown download gets: the document's filename, with `.md` added when it has no Markdown extension. */
export function markdownFilename(name: string): string {
  const base = bareName(name) ?? 'document';
  return /\.(md|markdown)$/i.test(base) ? base : `${base}.md`;
}

export function downloadTargetOf(item: Pick<CanvasItem, 'kind' | 'id' | 'title' | 'path'>): DownloadTarget {
  if (item.kind === 'document') return { kind: 'document', filename: markdownFilename(item.id) };
  if (item.kind === 'file') return { kind: 'file', url: `/api/files/${encodeURIComponent(item.id)}/raw`, filename: markdownFilename(item.title) };
  return { kind: 'app', path: `/apps/${item.id}/`, title: item.title, fallbackWord: item.kind === 'sketch' ? 'sketch' : 'deck' };
}

export const DOWNLOAD_TOOLTIP = 'Download';
export const DOWNLOAD_SAVED_TOOLTIP = 'Download the last saved version';
export const DOWNLOAD_NOT_READY = 'Download is available once the document has loaded.';

/**
 * The button's whole state in one decision (FRONT-1670): disabled iff there is a reason
 * why, and the tooltip is that reason or the action's name. An unsaved Edit draft never
 * changes what is downloaded (always the saved version), only what the tooltip says.
 */
export function downloadButtonState(input: {
  kind: CanvasItem['kind'];
  /** A document's saved body is loaded and can be written to a file. */
  documentLoaded: boolean;
  /** The Edit view holds text that is not saved yet. */
  draftUnsaved: boolean;
}): { disabled: boolean; tooltip: string } {
  if (input.kind === 'document' && !input.documentLoaded) return { disabled: true, tooltip: DOWNLOAD_NOT_READY };
  const text = input.kind === 'document' || input.kind === 'file';
  return { disabled: false, tooltip: text && input.draftUnsaved ? DOWNLOAD_SAVED_TOOLTIP : DOWNLOAD_TOOLTIP };
}
